import React, { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { useObservable } from 'react-use-observable';

import { Button, Modal } from 'antd';

import Spin from 'components/Spin';
import { Field, Form, Formik } from 'formik';
import type { FormikProps } from 'formik';
import TextFormField from 'v2/components/Shared/FormFields/TextFormField';
import NoDangerouslyHTML from 'v2/components/Shared/NoDangerouslyHTML';
import Truncate from 'v2/components/Shared/TruncateText';
import { dateMomentFormat } from 'v2/formatters/date';
import { ETicketStatus } from 'v2/interfaces/help';
import helpService from 'v2/services/help';

import { historyModalSchema } from './validation';

interface IProps {
  isOpen: boolean;
  handleClose: () => void;
}

interface IFormikValues {
  comment: string;
}

const HistoryModal = ({ isOpen, handleClose }: IProps) => {
  const historyChatRef = useRef<HTMLDivElement>();

  const [loadingTicket] = useObservable(() => helpService.getLoadingTicket(), []);
  const [selectedTicket] = useObservable(() => helpService.getTicket(), []);

  const onSubmit = ({ comment }: IFormikValues, { setSubmitting }: FormikProps<IFormikValues>) => {
    helpService.sendComment(selectedTicket.id, comment).subscribe(
      () => {
        toast.success('Comentário enviado com sucesso!');
        setSubmitting(false);
      },
      () => {
        toast.error('Erro ao enviar comentário!');
        setSubmitting(false);
      }
    );
  };

  useEffect(() => {
    const historyChatNode = historyChatRef.current;

    if (historyChatNode) {
      historyChatNode.scrollTop = historyChatNode.scrollHeight - historyChatNode.clientHeight;
    }
  }, [loadingTicket]);

  return (
    <Modal open={isOpen} onCancel={handleClose} title='Histórico do ticket' width={600} footer={null}>
      {loadingTicket && <Spin />}
      {!loadingTicket && (
        <div>
          <p className='text-base'>
            <Truncate count={50} final='...'>
              {selectedTicket?.title}
            </Truncate>
          </p>
          <div ref={historyChatRef} className='max-h-67.5 w-full overflow-auto rounded-[5px] bg-[#f9f9f9] p-5'>
            {selectedTicket?.comments?.map(comment => (
              <div
                key={'message-' + comment.id}
                className={`clear-both mb-3 w-auto max-w-75 rounded-sm text-sm leading-4 font-normal text-white last:mb-0 ${
                  comment.self ? 'float-right' : ''
                }`}
              >
                {!comment.self && <span className='block w-full text-[13px] text-[#999]'>Suporte Eduzz</span>}
                <NoDangerouslyHTML
                  className={`relative inline-block w-auto max-w-full rounded-[5px] px-4 py-2 font-normal ${
                    comment.self ? 'float-right mb-1 bg-[#86BB71]' : 'bg-[#94C2ED]'
                  }`}
                  html={comment.content}
                />
                <div
                  className={`clear-both w-full text-xs text-[#92959E] ${comment.self ? 'mt-0 text-right' : 'mt-1'}`}
                >
                  {dateMomentFormat(comment.created_at, 'DD/MM/YYYY HH:mm')}
                </div>
              </div>
            ))}
          </div>
          {selectedTicket?.status !== ETicketStatus.Fechado && (
            <div className='text-right'>
              <Formik initialValues={{ comment: '' }} onSubmit={onSubmit} validationSchema={historyModalSchema}>
                {({ dirty, isValid, isSubmitting }) => (
                  <Form>
                    <Field
                      name='comment'
                      placeholder='Digite um comentário...'
                      fullWidth
                      multiline
                      rows={1}
                      rowsMax={1}
                      inputProps={{
                        maxLength: 500
                      }}
                      component={TextFormField}
                    />
                    <div className='mt-4' />
                    <Button type='primary' disabled={!dirty || !isValid} htmlType='submit' loading={isSubmitting}>
                      Comentar
                    </Button>
                  </Form>
                )}
              </Formik>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default HistoryModal;
